import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router';
import type { AreaContext } from '../../components/layout/AppShell';
import { NotFoundSection } from '../NotFoundPage';
import { showConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import {
  deleteSystemProperty,
  listPropertyTypes,
  listSystemProperties,
  saveSystemProperty
} from '../../data/templates';
import { describeApiError } from '../../data/http';
import { getVariableCatalogue } from '../../data/templates';
import { listReferenceEntries } from '../../data/reference';
import { listServiceOptions } from '../../data/services';
import { referenceUseFor, serviceResponseTypeFor } from '../../components/studio/propertyConfigUi';
import { TokenField } from '../../components/studio/TokenField';
import { PropertyConfigFields } from '../../components/studio/PropertyConfigFields';
import { PropertyTypeSelect } from '../../components/studio/PropertyTypeSelect';
import { ExpressionText } from '../../components/studio/ExpressionText';
import { buildSections, type ReferenceOption } from '../../components/studio/variableSections';
import type { PropertyTypeDefinition, ServiceOption, SystemPropertySummary, VariableCatalogue } from '../../data/types';

const EMPTY_CATALOGUE: VariableCatalogue = { variables: [], transforms: [], expandable: {} };

/** Every use a reference entry can be bound to — same set the per-template Property Editor fetches. */
const REFERENCE_USES = ['text', 'image', 'table', 'document'];

function parseConfig(configJson: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(configJson || '{}');
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const blank: SystemPropertySummary = {
  tokenName: '',
  displayName: '',
  description: '',
  helpText: '',
  propertyType: 'Text',
  defaultValue: '',
  isAdvanced: false,
  sortOrder: 0,
  category: 'default',
  configJson: '{}'
};

const field =
  'mt-1 block w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

/**
 * RD-08 lives in the reference-data area rather than a settings page of its own:
 * one place to manage shared data. Editing is admin-only (the "system scope" in
 * the role model); authors reach the rest of reference data here too.
 */
export function SystemPropertiesPage() {
  const { roles } = useOutletContext<AreaContext>();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [properties, setProperties] = useState<SystemPropertySummary[]>([]);
  const [types, setTypes] = useState<PropertyTypeDefinition[]>([]);
  const [catalogue, setCatalogue] = useState<VariableCatalogue>(EMPTY_CATALOGUE);
  const [references, setReferences] = useState<Record<string, ReferenceOption[]>>({});
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [draft, setDraft] = useState<SystemPropertySummary>(blank);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tokenNameInputRef = useRef<HTMLInputElement>(null);

  // Edit populates the form above the table, which is easy to miss when the
  // table has scrolled the form out of view — make the change visible.
  const edit = (property: SystemPropertySummary) => {
    setDraft(property);
    tokenNameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    tokenNameInputRef.current?.focus();
  };

  const load = useCallback(
    () =>
      Promise.all([
        listSystemProperties(),
        listPropertyTypes(),
        getVariableCatalogue(),
        Promise.all(REFERENCE_USES.map((use) => listReferenceEntries({ use }))),
        listServiceOptions()
      ])
        .then(([saved, definitions, vocabulary, referenceLists, services]) => {
          setProperties(saved);
          setTypes(definitions);
          setCatalogue(vocabulary);
          setReferences(
            Object.fromEntries(
              REFERENCE_USES.map((use, i) => [
                use,
                (referenceLists[i]?.items ?? []).map((entry) => ({
                  key: entry.key,
                  scope: entry.scope,
                  title: entry.title,
                  description: entry.description,
                  kind: entry.kind
                }))
              ])
            )
          );
          setServiceOptions(services);
          setError(null);
        })
        .catch((err: unknown) => setError(describeApiError(err, t('errors.loadTemplates')))),
    [t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (!roles.includes('admin')) return <NotFoundSection />;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || draft.tokenName.trim().length === 0) return;

    setBusy(true);
    try {
      await saveSystemProperty({ ...draft, tokenName: draft.tokenName.trim() });
      toast({ title: t('settings.system.saved'), type: 'success' });
      setDraft(blank);
      await load();
    } catch (err) {
      toast({ title: describeApiError(err, t('errors.saveTemplate')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (property: SystemPropertySummary) => {
    const confirmed = await showConfirmModal({
      title: t('settings.system.deleteTitle'),
      description: t('settings.system.deleteDescription', { name: property.tokenName }),
      confirmLabel: t('common.delete'),
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteSystemProperty(property.tokenName);
      toast({ title: t('settings.system.deleted'), type: 'success' });
      await load();
    } catch (err) {
      toast({ title: describeApiError(err, t('errors.saveTemplate')), type: 'error' });
    }
  };

  const definition = types.find((d) => d.id === draft.propertyType);

  // Only earlier properties, ordered by sortOrder — the same rule the template editor applies.
  const sections = buildSections({
    catalogue,
    fields: properties
      .filter((property) => property.sortOrder < draft.sortOrder)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((property) => ({
        tokenName: property.tokenName,
        displayName: property.displayName || property.tokenName,
        propertyType: property.propertyType
      })),
    reference: references[referenceUseFor(draft.propertyType)] ?? [],
    currentFieldName: draft.displayName || draft.tokenName,
    t
  });

  return (
    <>
      <Link
        to="/studio/reference"
        className="text-sm font-semibold text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {t('reference.title')}
      </Link>

      <h1 className="mt-3 text-3xl font-heading font-semibold tracking-normal text-foreground">
        {t('settings.system.title')}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('settings.system.subtitle')}</p>

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <form onSubmit={submit} className="mt-6 rounded-lg border border-border bg-surface p-4">
        <h2 className="text-base font-semibold text-foreground">{t('settings.system.addTitle')}</h2>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium text-foreground">{t('settings.system.tokenName')}</span>
            <input
              ref={tokenNameInputRef}
              className={field}
              value={draft.tokenName}
              onChange={(event) => setDraft({ ...draft, tokenName: event.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-foreground">{t('properties.displayName')}</span>
            <input
              className={field}
              value={draft.displayName}
              onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-foreground">{t('properties.type')}</span>
            <PropertyTypeSelect
              types={types}
              value={draft.propertyType}
              onChange={(next) => setDraft({ ...draft, propertyType: next, configJson: '{}' })}
            />
          </label>
          <TokenField
            label={t('properties.defaultValue')}
            value={draft.defaultValue}
            onChange={(next) => setDraft({ ...draft, defaultValue: next })}
            transforms={catalogue.transforms}
            sections={sections}
          />
        </div>

        {definition && definition.configSchema.length > 0 ? (
          <div className="mt-3 border-t border-border pt-3">
            <PropertyConfigFields
              schema={definition.configSchema}
              config={parseConfig(draft.configJson)}
              onChange={(next) => setDraft({ ...draft, configJson: JSON.stringify(next) })}
              defaultValue={draft.defaultValue}
              tableReferenceOptions={references.table ?? []}
              imageReferenceOptions={references.image ?? []}
              services={serviceOptions.filter((s) => s.responseType === serviceResponseTypeFor(draft.propertyType))}
              variableSections={sections}
              valueSources={definition.valueSources}
              onDefaultValueChange={(next) => setDraft({ ...draft, defaultValue: next })}
            />
          </div>
        ) : null}

        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.isAdvanced}
            onChange={(event) => setDraft({ ...draft, isAdvanced: event.target.checked })}
            className="size-4 rounded border-input"
          />
          <span className="font-medium text-foreground">{t('properties.advanced')}</span>
        </label>

        <button
          type="submit"
          disabled={busy}
          className="mt-4 inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          {t('settings.system.save')}
        </button>
      </form>

      {properties.length > 0 ? (
        <div className="mt-6 overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted">
              <tr>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">
                  {t('settings.system.tokenName')}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">
                  {t('properties.type')}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">
                  {t('properties.defaultValue')}
                </th>
                <th scope="col" className="px-4 py-2.5 text-right font-semibold text-foreground">
                  {t('library.documents.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {properties.map((property) => (
                <tr key={property.tokenName} className="hover:bg-muted">
                  <td className="px-4 py-3 font-semibold text-foreground">{property.tokenName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t(`propertyTypes.${property.propertyType}`)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <ExpressionText value={property.defaultValue} tokenName={property.tokenName} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => edit(property)}
                        className="rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        {t('settings.system.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(property)}
                        aria-label={t('settings.system.deleteLabel', { name: property.tokenName })}
                        className="inline-flex items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-destructive transition hover:bg-destructive-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        <TrashIcon className="size-4" aria-hidden="true" />
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
